import React, { useState, useEffect, useCallback } from 'react';
import { Building2, Plus, Pencil, Trash2, Loader2, X, Check } from 'lucide-react';
import { authAPI, isPlatformAdmin } from '../lib/auth';

const EMPTY_FORM = {
  tenant_code: '', tenant_name: '', contact_email: '', contact_phone: '',
  subscription_tier: 'standard', max_users: 10, max_merchants_per_month: 1000,
  address: '', billing_contact_name: '', billing_contact_email: '',
  business_owner_name: '', business_owner_email: '', start_date: '',
  admin_email: '', admin_password: '', admin_name: '',
};

export default function TenantManagementPage() {
  const [tenants, setTenants] = useState([]);
  const [loading, setLoading] = useState(true);
  const [modalOpen, setModalOpen] = useState(false);
  const [editing, setEditing] = useState(null); // null = create, object = edit
  const [form, setForm] = useState(EMPTY_FORM);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState(null);
  const isAdmin = isPlatformAdmin();

  const loadTenants = useCallback(async () => {
    setLoading(true);
    try {
      const res = await authAPI.listTenants();
      setTenants(res.data.tenants || []);
    } catch {
      setError('Failed to load tenants');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { loadTenants(); }, [loadTenants]);

  const openCreate = () => {
    setEditing(null);
    setForm(EMPTY_FORM);
    setError(null);
    setModalOpen(true);
  };

  const openEdit = (tenant) => {
    setEditing(tenant);
    setForm({
      ...EMPTY_FORM,
      tenant_code: tenant.tenant_code || '',
      tenant_name: tenant.tenant_name || '',
      contact_email: tenant.contact_email || '',
      contact_phone: tenant.contact_phone || '',
      subscription_tier: tenant.subscription_tier || 'standard',
      max_users: tenant.max_users || 10,
      max_merchants_per_month: tenant.max_merchants_per_month || 1000,
      address: tenant.address || '',
      billing_contact_name: tenant.billing_contact_name || '',
      billing_contact_email: tenant.billing_contact_email || '',
      business_owner_name: tenant.business_owner_name || '',
      business_owner_email: tenant.business_owner_email || '',
      start_date: tenant.start_date ? tenant.start_date.split('T')[0] : '',
    });
    setError(null);
    setModalOpen(true);
  };

  const handleSave = async () => {
    setSaving(true);
    setError(null);
    try {
      if (editing) {
        await authAPI.updateTenant(editing.id, form);
      } else {
        await authAPI.createTenant(form);
      }
      setModalOpen(false);
      loadTenants();
    } catch (err) {
      setError(err.response?.data?.detail || 'Failed to save');
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = async (tenant) => {
    if (!window.confirm(`Delete tenant "${tenant.tenant_name}"? This will also delete all its users.`)) return;
    try {
      await authAPI.deleteTenant(tenant.id);
      loadTenants();
    } catch (err) {
      alert(err.response?.data?.detail || 'Failed to delete');
    }
  };

  const set = (key) => (e) => setForm(f => ({ ...f, [key]: e.target.value }));

  if (!isAdmin) {
    return (
      <div className="py-16 text-center text-gray-500">
        <Building2 className="w-12 h-12 mx-auto mb-4 text-gray-300" />
        <h2 className="text-lg font-semibold">Access Denied</h2>
        <p className="text-sm">Only platform administrators can manage tenants.</p>
      </div>
    );
  }

  return (
    <div>
      {/* Header */}
      <div className="flex items-center justify-between mb-6">
        <div>
          <h1 className="text-2xl font-bold text-gray-900 flex items-center gap-2">
            <Building2 className="w-6 h-6 text-blue-600" />
            Tenant Management
          </h1>
          <p className="text-sm text-gray-500 mt-1">Manage platform tenants and their configurations</p>
        </div>
        <button
          onClick={openCreate}
          className="flex items-center gap-2 px-4 py-2 bg-blue-600 text-white rounded-lg text-sm font-medium hover:bg-blue-700 transition-colors shadow-sm"
        >
          <Plus className="w-4 h-4" />
          Add Tenant
        </button>
      </div>

      {/* Table */}
      {loading ? (
        <div className="flex justify-center py-12"><Loader2 className="w-6 h-6 animate-spin text-gray-400" /></div>
      ) : (
        <div className="bg-white rounded-xl border border-gray-200 shadow-sm overflow-hidden">
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead className="bg-gray-50 border-b border-gray-200">
                <tr>
                  <th className="text-left px-4 py-3 font-semibold text-gray-600">Tenant</th>
                  <th className="text-left px-4 py-3 font-semibold text-gray-600">Code</th>
                  <th className="text-left px-4 py-3 font-semibold text-gray-600">Contact</th>
                  <th className="text-left px-4 py-3 font-semibold text-gray-600">Tier</th>
                  <th className="text-center px-4 py-3 font-semibold text-gray-600">Users</th>
                  <th className="text-center px-4 py-3 font-semibold text-gray-600">Status</th>
                  <th className="text-right px-4 py-3 font-semibold text-gray-600">Actions</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-100">
                {tenants.map(t => (
                  <tr key={t.id} className="hover:bg-gray-50">
                    <td className="px-4 py-3">
                      <p className="font-medium text-gray-900">{t.tenant_name}</p>
                      {t.business_owner_name && (
                        <p className="text-xs text-gray-400">{t.business_owner_name}</p>
                      )}
                    </td>
                    <td className="px-4 py-3">
                      <span className="px-2 py-0.5 bg-gray-100 rounded text-xs font-mono">{t.tenant_code}</span>
                    </td>
                    <td className="px-4 py-3 text-gray-600">{t.contact_email || '—'}</td>
                    <td className="px-4 py-3">
                      <span className="capitalize text-gray-700">{t.subscription_tier || 'standard'}</span>
                    </td>
                    <td className="px-4 py-3 text-center text-gray-600">
                      {t.user_count != null ? t.user_count : '—'}
                    </td>
                    <td className="px-4 py-3 text-center">
                      <span className={`inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-xs font-medium ${
                        t.is_active ? 'bg-green-50 text-green-700' : 'bg-red-50 text-red-700'
                      }`}>
                        {t.is_active ? <Check className="w-3 h-3" /> : <X className="w-3 h-3" />}
                        {t.is_active ? 'Active' : 'Inactive'}
                      </span>
                    </td>
                    <td className="px-4 py-3 text-right">
                      <div className="flex justify-end gap-1">
                        <button
                          onClick={() => openEdit(t)}
                          className="p-1.5 rounded-lg text-gray-400 hover:text-blue-600 hover:bg-blue-50"
                          title="Edit"
                        >
                          <Pencil className="w-4 h-4" />
                        </button>
                        {t.tenant_code !== 'paylegit' && (
                          <button
                            onClick={() => handleDelete(t)}
                            className="p-1.5 rounded-lg text-gray-400 hover:text-red-600 hover:bg-red-50"
                            title="Delete"
                          >
                            <Trash2 className="w-4 h-4" />
                          </button>
                        )}
                      </div>
                    </td>
                  </tr>
                ))}
                {tenants.length === 0 && (
                  <tr>
                    <td colSpan={7} className="px-4 py-12 text-center text-gray-400">
                      No tenants found. Click "Add Tenant" to create one.
                    </td>
                  </tr>
                )}
              </tbody>
            </table>
          </div>
        </div>
      )}

      {/* Create/Edit Modal */}
      {modalOpen && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40 backdrop-blur-sm">
          <div className="bg-white rounded-2xl shadow-xl w-full max-w-2xl mx-4 max-h-[90vh] overflow-auto">
            <div className="flex items-center justify-between px-6 py-4 border-b border-gray-100">
              <h3 className="text-lg font-bold text-gray-900">
                {editing ? 'Edit Tenant' : 'Create Tenant'}
              </h3>
              <button onClick={() => setModalOpen(false)} className="text-gray-400 hover:text-gray-600">
                <X className="w-5 h-5" />
              </button>
            </div>
            <div className="px-6 py-4 space-y-4">
              {error && (
                <div className="rounded-lg border border-red-200 bg-red-50 px-3 py-2 text-sm text-red-700">{error}</div>
              )}

              <div className="grid grid-cols-2 gap-4">
                <Field label="Tenant Code" value={form.tenant_code} onChange={set('tenant_code')}
                       placeholder="e.g. payu" disabled={!!editing} />
                <Field label="Tenant Name" value={form.tenant_name} onChange={set('tenant_name')}
                       placeholder="PayU India" />
                <Field label="Contact Email" value={form.contact_email} onChange={set('contact_email')}
                       placeholder="ops@payu.in" type="email" />
                <Field label="Contact Phone" value={form.contact_phone} onChange={set('contact_phone')}
                       placeholder="+91..." />
                <Field label="Subscription Tier" value={form.subscription_tier}
                       onChange={set('subscription_tier')} select={['standard', 'professional', 'enterprise']} />
                <Field label="Max Users" value={form.max_users} onChange={set('max_users')} type="number" />
                <Field label="Max Merchants/Month" value={form.max_merchants_per_month}
                       onChange={set('max_merchants_per_month')} type="number" />
                <Field label="Start Date" value={form.start_date} onChange={set('start_date')} type="date" />
              </div>

              <div>
                <Field label="Address" value={form.address} onChange={set('address')} textarea />
              </div>

              <div className="grid grid-cols-2 gap-4">
                <Field label="Billing Contact Name" value={form.billing_contact_name}
                       onChange={set('billing_contact_name')} />
                <Field label="Billing Contact Email" value={form.billing_contact_email}
                       onChange={set('billing_contact_email')} type="email" />
                <Field label="Business Owner Name" value={form.business_owner_name}
                       onChange={set('business_owner_name')} />
                <Field label="Business Owner Email" value={form.business_owner_email}
                       onChange={set('business_owner_email')} type="email" />
              </div>

              {/* Initial admin (only for create) */}
              {!editing && (
                <>
                  <div className="border-t border-gray-100 pt-4 mt-4">
                    <p className="text-sm font-semibold text-gray-700 mb-3">Initial Tenant Admin (optional)</p>
                    <div className="grid grid-cols-2 gap-4">
                      <Field label="Admin Name" value={form.admin_name} onChange={set('admin_name')}
                             placeholder="John Doe" />
                      <Field label="Admin Email" value={form.admin_email} onChange={set('admin_email')}
                             placeholder="john@payu.in" type="email" />
                      <Field label="Admin Password" value={form.admin_password} onChange={set('admin_password')}
                             type="password" colSpan={2} />
                    </div>
                  </div>
                </>
              )}
            </div>
            <div className="flex justify-end gap-3 px-6 py-4 border-t border-gray-100">
              <button
                onClick={() => setModalOpen(false)}
                className="px-4 py-2 rounded-lg text-sm font-medium text-gray-600 hover:bg-gray-100"
              >
                Cancel
              </button>
              <button
                onClick={handleSave}
                disabled={saving || !form.tenant_code || !form.tenant_name}
                className="flex items-center gap-2 px-4 py-2 bg-blue-600 text-white rounded-lg text-sm font-medium hover:bg-blue-700 disabled:opacity-50 transition-colors"
              >
                {saving && <Loader2 className="w-4 h-4 animate-spin" />}
                {editing ? 'Save Changes' : 'Create Tenant'}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

function Field({ label, value, onChange, placeholder, type = 'text', disabled, select, textarea, colSpan }) {
  const cls = `w-full px-3 py-2 border border-gray-200 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent disabled:bg-gray-50 disabled:text-gray-400`;
  return (
    <div className={colSpan === 2 ? 'col-span-2' : ''}>
      <label className="block text-xs font-semibold text-gray-600 uppercase tracking-wider mb-1">{label}</label>
      {select ? (
        <select value={value} onChange={onChange} className={cls}>
          {select.map(o => <option key={o} value={o} className="capitalize">{o}</option>)}
        </select>
      ) : textarea ? (
        <textarea value={value} onChange={onChange} placeholder={placeholder} rows={2} className={cls} />
      ) : (
        <input type={type} value={value} onChange={onChange} placeholder={placeholder}
               disabled={disabled} className={cls} />
      )}
    </div>
  );
}
